import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import allServices from "@/data/services";

const serviceGroups = [
  { label: "Core Services", items: allServices.filter(s => s.category === "core") },
  { label: "Emerging Technologies", items: allServices.filter(s => s.category === "emerging") },
  { label: "Lifecycle & Operations", items: allServices.filter(s => s.category === "lifecycle") },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 200);
  };

  return (
    <nav data-testid="navbar" className="nav-clean fixed top-0 left-0 right-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex flex-col items-center gap-0.5">
            <img src="https://hostdost.in/logo.png" alt="Vaana BuildLogic Technologies" className="h-10 w-auto" />
            <span className="hidden sm:block text-[#0D72B8] font-bold text-[10px] tracking-[0.25em] font-['Sora'] w-full text-center">Vaana BuildLogic</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/" data-testid="nav-link-home"
              className={`text-[15px] font-medium transition-colors duration-300 ${isActive("/") && location.pathname === "/" ? "text-[#0D72B8]" : "text-[#4B5563] hover:text-[#0D72B8]"}`}>
              Home
            </Link>
            <Link to="/about" data-testid="nav-link-about-us"
              className={`text-[15px] font-medium transition-colors duration-300 ${isActive("/about") ? "text-[#0D72B8]" : "text-[#4B5563] hover:text-[#0D72B8]"}`}>
              About Us
            </Link>

            {/* Services Dropdown */}
            <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <Link to="/services" data-testid="nav-link-services"
                className={`flex items-center gap-1 text-[15px] font-medium transition-colors duration-300 ${isActive("/services") ? "text-[#0D72B8]" : "text-[#4B5563] hover:text-[#0D72B8]"}`}>
                Services
                <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
              </Link>

              {servicesOpen && (
                <div data-testid="services-dropdown" className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[720px] bg-white rounded-2xl shadow-2xl shadow-black/10 border border-gray-100 p-6 grid grid-cols-3 gap-6"
                  onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                  {serviceGroups.map((group) => (
                    <div key={group.label}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#0D72B8] mb-3">{group.label}</p>
                      <ul className="space-y-1">
                        {group.items.map((s) => (
                          <li key={s.slug}>
                            <Link to={`/services/${s.slug}`}
                              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-[#4B5563] hover:bg-blue-50 hover:text-[#0D72B8] transition-all group">
                              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 group-hover:bg-[#0D72B8]/10 transition-colors flex-shrink-0">
                                <s.icon size={14} className="text-[#0D72B8]" strokeWidth={1.5} />
                              </div>
                              <span className="font-medium">{s.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <div className="col-span-3 pt-4 mt-2 border-t border-gray-100">
                    <Link to="/services" className="text-sm font-semibold text-[#0D72B8] hover:text-[#0B5898] transition-colors">
                      View all services &rarr;
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" data-testid="nav-link-contact"
              className={`text-[15px] font-medium transition-colors duration-300 ${isActive("/contact") ? "text-[#0D72B8]" : "text-[#4B5563] hover:text-[#0D72B8]"}`}>
              Contact
            </Link>
            <Link to="/contact" data-testid="nav-cta-button" className="px-6 py-2.5 btn-outline-blue text-sm font-semibold rounded-full">
              Get Started
            </Link>
          </div>

          <button data-testid="mobile-menu-toggle" className="md:hidden text-[#0D72B8]" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div data-testid="mobile-menu" className="md:hidden pb-6 border-t border-gray-100 mt-2 pt-4 max-h-[80vh] overflow-y-auto">
            <Link to="/" onClick={() => setMobileOpen(false)} className={`block py-3 text-sm font-medium ${location.pathname === "/" ? "text-[#0D72B8]" : "text-[#4B5563]"}`}>Home</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className={`block py-3 text-sm font-medium ${isActive("/about") ? "text-[#0D72B8]" : "text-[#4B5563]"}`}>About Us</Link>

            {/* Mobile Services Accordion */}
            <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className={`w-full flex items-center justify-between py-3 text-sm font-medium ${isActive("/services") ? "text-[#0D72B8]" : "text-[#4B5563]"}`}>
              Services
              <ChevronDown size={14} className={`transition-transform duration-200 ${mobileServicesOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 pb-2">
                <Link to="/services" onClick={() => setMobileOpen(false)} className="block py-2 text-xs font-semibold text-[#0D72B8]">View All Services</Link>
                {serviceGroups.map((group) => (
                  <div key={group.label} className="mt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF] mb-1">{group.label}</p>
                    {group.items.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 py-1.5 text-xs text-[#4B5563] hover:text-[#0D72B8]">
                        <s.icon size={12} className="text-[#0D72B8]" strokeWidth={1.5} />
                        {s.title}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <Link to="/contact" onClick={() => setMobileOpen(false)} className={`block py-3 text-sm font-medium ${isActive("/contact") ? "text-[#0D72B8]" : "text-[#4B5563]"}`}>Contact</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="mt-3 inline-block px-6 py-2.5 btn-outline-blue text-sm font-semibold rounded-full">Get Started</Link>
          </div>
        )}
      </div>
    </nav>
  );
}
